import Konva from 'konva'
import { ref } from 'vue'
import { EUROPE_DEFAULT_IMAGE } from './defaultImages.js'

export function useKonvaCanvas(canvasRef, selectedElement, selectedZoneId, zones, saveElement) {
  const stage = ref(null)
  const zoneLayer = ref(null)

  let isInitializing = false
  let retryCount = 0
  const MAX_RETRIES = 5
  let onStageReadyCallback = null

  function initStage(onReady) {
    if (onReady) onStageReadyCallback = onReady
    if (isInitializing) return
    isInitializing = true

    if (!canvasRef.value) {
      console.warn('⚠️ Canvas container not ready, retrying...')
      retryCount++
      if (retryCount <= MAX_RETRIES) {
        setTimeout(() => {
          isInitializing = false
          initStage()
        }, 200)
      } else {
        console.error('⛔ Échec de l\'initialisation du stage après plusieurs tentatives')
        isInitializing = false
      }
      return
    }

    const imageObj = new Image()
    imageObj.onload = () => {
      if (!canvasRef.value) {
        console.warn('⚠️ Canvas container still not ready after image load, aborting')
        isInitializing = false
        return
      }

      // Utiliser les dimensions stockées si elles existent, sinon les dimensions de l'image
      const stageWidth = selectedElement.value?.el_CanvasSize?.width || imageObj.width
      const stageHeight = selectedElement.value?.el_CanvasSize?.height || imageObj.height

      stage.value = new Konva.Stage({
        container: canvasRef.value,
        width: stageWidth,
        height: stageHeight,
      })

      // Sauvegarder les dimensions si elles n'existent pas encore
      if (!selectedElement.value.el_CanvasSize) {
        selectedElement.value.el_CanvasSize = {
          width: stageWidth,
          height: stageHeight
        }
      }

      const bgLayer = new Konva.Layer()
      const image = new Konva.Image({
        image: imageObj,
        width: stageWidth,
        height: stageHeight,
      })
      bgLayer.add(image)
      stage.value.add(bgLayer)

      zoneLayer.value = new Konva.Layer()
      stage.value.add(zoneLayer.value)

      isInitializing = false
      retryCount = 0

      // Appeler le callback si défini
      if (onStageReadyCallback) {
        onStageReadyCallback()
        onStageReadyCallback = null
      }
    }

    // Fonction helper pour obtenir la source d'image (même logique que Preview.vue)
    function getBackgroundImageSource() {
      // 1. Image Base64 stockée
      if (selectedElement.value?.el_data?.backgroundImage?.base64) {
        console.log('✅ Editor - Image Base64 utilisateur');
        return selectedElement.value.el_data.backgroundImage.base64;
      }

      // 2. Image personnalisée (rétrocompatibilité)
      if (selectedElement.value?.el_Background && 
          selectedElement.value.el_Background !== '/media/europe.png') {
        
        const urlbase = import.meta.env.VITE_BASE_EDITOR || window.location.origin;
        const rawSrc = selectedElement.value.el_Background;
        const badurl = urlbase + rawSrc;
        const fixedUrl = badurl.replace(/([^:]\/)\/+/g, '$1');
        
        console.log('🔄 Editor - Image personnalisée:', fixedUrl);
        return fixedUrl;
      }

      // 3. Image par défaut intégrée
      console.log('🌍 Editor - Image par défaut europe.png');
      return EUROPE_DEFAULT_IMAGE;
    }

    const imageSrc = getBackgroundImageSource();
    imageObj.src = imageSrc;
    
    // Gestion d'erreur pour le chargement d'image
    imageObj.onerror = () => {
      console.error("❌ Erreur lors du chargement de l'image:", imageSrc.substring(0, 50) + "...");
      
      // FALLBACK : Si l'image personnalisée échoue, utiliser l'image par défaut
      if (imageSrc !== EUROPE_DEFAULT_IMAGE) {
        console.log("🔄 Basculement vers l'image par défaut");
        imageObj.src = EUROPE_DEFAULT_IMAGE;
      } else {
        // Même l'image par défaut a échoué - créer un canvas vide
        console.error("❌ Impossible de charger l'image par défaut, création d'un canvas vide");
        
        stage.value = new Konva.Stage({
          container: canvasRef.value,
          width: 800,
          height: 600,
        })

        const bgLayer = new Konva.Layer()
        const backgroundRect = new Konva.Rect({
          width: 800,
          height: 600,
          fill: '#f5f5f5',
          stroke: '#ddd',
          strokeWidth: 1
        })
        bgLayer.add(backgroundRect)
        stage.value.add(bgLayer)

        zoneLayer.value = new Konva.Layer()
        stage.value.add(zoneLayer.value)

        isInitializing = false
        retryCount = 0

        // Appeler le callback si défini
        if (onStageReadyCallback) {
          onStageReadyCallback()
          onStageReadyCallback = null
        }
      }
    };
  }

  function drawExistingZones(zonesArray) {
    if (!zoneLayer.value) {
      console.warn('⚠️ drawExistingZones: zoneLayer not initialized yet')
      return
    }
    console.log(`📍 Drawing ${zonesArray.length} existing zones`)
    zoneLayer.value.destroyChildren()
    zonesArray.forEach(zone => {
      if (drawZoneHandler) {
        drawZoneHandler(zone)
      }
    })
    zoneLayer.value.draw()
    console.log('✅ Zones drawn successfully')
  }

  function deleteSelectedZone() {
    if (!selectedZoneId.value) return
    
    const shape = zoneLayer.value.findOne(`#${selectedZoneId.value}`)
    if (shape) {
      // Supprimer les transformers existants
      zoneLayer.value.find('Transformer').forEach(tr => tr.destroy())
      
      // Supprimer la forme
      shape.destroy()
      
      // Supprimer la zone des données
      zones.value = zones.value.filter(z => z.id !== selectedZoneId.value)
      
      // Réinitialiser la sélection
      selectedZoneId.value = null
      
      // Redessiner le layer
      zoneLayer.value.draw()
      
      // Sauvegarder
      saveElement()
      
      console.log("Zone supprimée avec succès")
    } else {
      console.warn("Forme non trouvée pour l'ID:", selectedZoneId.value)
    }
  }

  let drawZoneHandler = null;
  
  function registerDrawZone(drawZoneFn) {
    drawZoneHandler = drawZoneFn;
  }

  function setBackgroundImage(imageDataUrl) {
    if (!stage.value || !zoneLayer.value) {
      if (isInitializing) {
        console.warn("⏳ Attente de l'initialisation du stage, setBackgroundImage annulé temporairement")
        return
      }
      
      // Ne pas essayer de ré-initialiser si on n'a pas de données d'image dans selectedElement
      const hasImageData = selectedElement.value?.el_data?.backgroundImage?.base64
      if (!hasImageData && !imageDataUrl) {
        console.warn("⚠️ Pas d'image disponible pour initialiser le stage")
        return
      }
      
      console.log("Stage ou zoneLayer non initialisé. Re-initialisation...")
      initStage()
      setTimeout(() => setBackgroundImage(imageDataUrl), 200)
      return
    }

    const imageObj = new window.Image()
    imageObj.onload = () => {
      const bgLayer = stage.value.findOne(layer => layer instanceof Konva.Layer)
      if (!bgLayer) return

      // Sauvegarder les nouvelles dimensions dans l'élément
      if (!selectedElement.value.el_CanvasSize) {
        selectedElement.value.el_CanvasSize = {}
      }
      selectedElement.value.el_CanvasSize.width = imageObj.width
      selectedElement.value.el_CanvasSize.height = imageObj.height

      // Mettre à jour les dimensions du stage
      stage.value.width(imageObj.width)
      stage.value.height(imageObj.height)

      // Mettre à jour l'image de fond
      bgLayer.destroyChildren()
      const konvaImage = new Konva.Image({
        image: imageObj,
        width: imageObj.width,
        height: imageObj.height,
      })
      bgLayer.add(konvaImage)
      
      // Redessiner le stage complet
      stage.value.draw()
      
      // Sauvegarder automatiquement les changements
      if (saveElement) {
        saveElement()
      }
      
      console.log(`✅ Image mise à jour avec nouvelles dimensions: ${imageObj.width}x${imageObj.height}`)
    }
    imageObj.src = imageDataUrl
  }

  function isZoomLocked() {
    // placeholder si tu as une logique de zoom
    return false
  }

  function toggleZoomLock() {
    // idem
  }

  function clearSelection() {
    selectedZoneId.value = null;
  }

  return {
    stage,
    zoneLayer,
    initStage,
    drawExistingZones,
    deleteSelectedZone,
    registerDrawZone,
    setBackgroundImage,
    isZoomLocked,
    toggleZoomLock,
    clearSelection
  }
}
